/**
 * useWorkflowTemplate Hook
 *
 * Fetches Metadata DSL v1.0 workflow templates from backend for dynamic form generation.
 *
 * FERP Principles:
 * - Data-driven: All form definitions from backend metadata
 * - Domain-agnostic: Templates work for any domain (healthcare, finance, legal, etc.)
 * - Life-critical: Evidence attribution + confidence scoring for transparency
 * - Single code path: Repository → GraphQL → This hook → MetadataDrivenForm
 *
 * Features:
 * - Fetch single template by ID (for form generation)
 * - List all templates for tenant (for dynamic-form-selector)
 * - Filter by domain and category
 * - Evidence attribution tracking
 * - Confidence scoring for AI-generated templates
 * - Sample Task templates merged with backend templates (Phase 1 integration)
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { SAMPLE_TASK_TEMPLATES } from '../task/templates/sampleTaskTemplates';

// ============================================================================
// TYPESCRIPT TYPES (matches GraphQL schema)
// ============================================================================

export interface WorkflowTemplateMetadata {
  templateId: string;
  templateVersion: string;
  schemaVersion: string;
  tenant: string;
  domain: string;
  templateType: string;
  metadata: TemplateMetadataContent;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  versionHistory: VersionEntry[];
  usageCount: number;
  lastUsedAt?: string;
  evidence?: Record<string, FieldEvidence>;
  confidence?: number;
  extractionMetadata?: ExtractionMetadata;
}

export interface TemplateMetadataContent {
  business: BusinessMetadata;
  ui: UIMetadata;
  primitives: PrimitiveComposition;
  mappings: Record<string, unknown>;
  compliance: ComplianceMetadata;
}

export interface BusinessMetadata {
  name: string;
  domain: string;
  category: string;
  description: string;
  icon?: string;
  tags: string[];
}

export interface UIMetadata {
  layout: string; // "wizard" | "single_page" | "tabbed"
  steps: UIStep[];
}

export interface UIStep {
  id: string;
  label: string;
  description?: string;
  fields: UIField[];
}

export interface UIField {
  id: string;
  component: string; // "text" | "number" | "select" | "date" | "checkbox" | "textarea"
  label: string;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  dataSource?: DataSource;
  validation?: ValidationRules;
  mapsTo: PrimitiveMapping[];
}

export interface DataSource {
  type: string; // "static" | "parameter" | "graphql"
  staticOptions?: string[] | SelectOption[]; // Mantine components accept both
  parameterName?: string;
  graphqlQuery?: string;
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface ValidationRules {
  type: string; // "string" | "number" | "date" | "email" | "phone"
  min?: number | string;
  max?: number | string;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  customValidator?: string;
  errorMessage?: string;
  requiredIf?: string; // JSONLogic expression
}

export interface PrimitiveMapping {
  primitiveType: string;
  primitiveId: string;
  fieldPath: string;
  transformation?: Record<string, unknown>;
  auditTracking?: Record<string, unknown>;
}

export interface PrimitiveComposition {
  nodes: PrimitiveNode[];
  connections: PrimitiveConnection[];
}

export interface PrimitiveNode {
  id: string;
  type: string; // One of 16 FERP primitive types
  name: string;
  position?: { x: number; y: number };
  metadata?: Record<string, unknown>;
}

export interface PrimitiveConnection {
  id: string;
  source: string;
  target: string;
  connectionType?: string;
}

export interface ComplianceMetadata {
  frameworks: string[];
  auditLevel: string;
}

export interface VersionEntry {
  version: string;
  updatedAt: string;
  changes: string;
}

export interface FieldEvidence {
  extractionStepId: string;
  sourceDocumentId: string;
  sourceType: string;
  pageNumber?: number;
  sourceLocation: string;
  extractedText: string;
  confidence: number;
  llmProvider: string;
}

export interface ExtractionMetadata {
  extractionId: string;
  sourceDocuments: string[];
  llmEnsemble: string[];
  overallConfidence: number;
  extractedAt: string;
  requiresHumanReview: boolean;
}

export interface TemplateListItem {
  templateId: string;
  templateVersion: string;
  domain: string;
  templateType: string;
  name: string;
  category: string;
  description: string;
  icon?: string;
  tags: string[];
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// GRAPHQL QUERIES
// ============================================================================

const GET_WORKFLOW_TEMPLATE = gql`
  query GetWorkflowTemplate($tenant: String!, $templateId: String!, $version: String!) {
    getTemplateMetadata(tenant: $tenant, templateId: $templateId, version: $version) {
      templateId
      templateVersion
      schemaVersion
      tenant
      domain
      templateType
      metadata {
        business {
          name
          domain
          category
          description
          icon
          tags
        }
        ui {
          layout
          steps {
            id
            label
            description
            fields {
              id
              component
              label
              required
              placeholder
              helpText
              dataSource
              validation
              mapsTo {
                primitiveType
                primitiveId
                fieldPath
                transformation
                auditTracking
              }
            }
          }
        }
        primitives {
          nodes {
            id
            type
            name
            position
            metadata
          }
          connections {
            id
            source
            target
            connectionType
          }
        }
        mappings
        compliance {
          frameworks
          auditLevel
        }
      }
      createdAt
      createdBy
      updatedAt
      updatedBy
      versionHistory {
        version
        updatedAt
        changes
      }
      usageCount
      lastUsedAt
      evidence
      confidence
      extractionMetadata {
        extractionId
        sourceDocuments
        llmEnsemble
        overallConfidence
        extractedAt
        requiresHumanReview
      }
    }
  }
`;

const GET_LATEST_WORKFLOW_TEMPLATE = gql`
  query GetLatestWorkflowTemplate($tenant: String!, $templateId: String!) {
    getLatestTemplateMetadata(tenant: $tenant, templateId: $templateId) {
      templateId
      templateVersion
      schemaVersion
      tenant
      domain
      templateType
      metadata {
        business {
          name
          domain
          category
          description
          icon
          tags
        }
        ui {
          layout
          steps {
            id
            label
            description
            fields {
              id
              component
              label
              required
              placeholder
              helpText
              dataSource
              validation
              mapsTo {
                primitiveType
                primitiveId
                fieldPath
                transformation
                auditTracking
              }
            }
          }
        }
        primitives {
          nodes {
            id
            type
            name
            position
            metadata
          }
          connections {
            id
            source
            target
            connectionType
          }
        }
        mappings
        compliance {
          frameworks
          auditLevel
        }
      }
      createdAt
      createdBy
      updatedAt
      updatedBy
      versionHistory {
        version
        updatedAt
        changes
      }
      usageCount
      lastUsedAt
      evidence
      confidence
      extractionMetadata {
        extractionId
        sourceDocuments
        llmEnsemble
        overallConfidence
        extractedAt
        requiresHumanReview
      }
    }
  }
`;

const LIST_WORKFLOW_TEMPLATES_BY_DOMAIN = gql`
  query ListWorkflowTemplatesByDomain($domain: String!, $templateType: String, $limit: Int) {
    listTemplatesByDomain(domain: $domain, templateType: $templateType, limit: $limit) {
      templateId
      templateVersion
      domain
      templateType
      name
      category
      description
      icon
      tags
      usageCount
      createdAt
      updatedAt
    }
  }
`;

const LIST_WORKFLOW_TEMPLATES_BY_TENANT = gql`
  query ListWorkflowTemplatesByTenant($tenant: String!, $limit: Int) {
    listTemplatesByTenant(tenant: $tenant, limit: $limit) {
      templateId
      templateVersion
      domain
      templateType
      name
      category
      description
      icon
      tags
      usageCount
      createdAt
      updatedAt
    }
  }
`;

// ============================================================================
// REACT HOOKS
// ============================================================================

export interface UseWorkflowTemplateOptions {
  tenant: string;
  templateId: string;
  version?: string; // If not provided, fetch latest version
  skip?: boolean;
}

export interface UseWorkflowTemplateResult {
  template: WorkflowTemplateMetadata | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch single workflow template by ID and version
 *
 * Usage:
 * ```typescript
 * const { template, loading, error } = useWorkflowTemplate({
 *   tenant: 'test',
 *   templateId: 'patient-admission',
 *   version: '1.0.0' // Optional - fetches latest if not provided
 * });
 * ```
 */
export function useWorkflowTemplate({
  tenant,
  templateId,
  version,
  skip = false
}: UseWorkflowTemplateOptions): UseWorkflowTemplateResult {
  // If version provided, use specific version query
  const specificQuery = useQuery<Record<string, any>>(GET_WORKFLOW_TEMPLATE, {
    variables: { tenant, templateId, version },
    skip: skip || !version
  });

  // If no version provided, use latest version query
  const latestQuery = useQuery<Record<string, any>>(GET_LATEST_WORKFLOW_TEMPLATE, {
    variables: { tenant, templateId },
    skip: skip || !!version
  });

  // Return whichever query is active
  const activeQuery = version ? specificQuery : latestQuery;

  // Get backend template
  const backendTemplate = activeQuery.data?.getTemplateMetadata || activeQuery.data?.getLatestTemplateMetadata || null;

  // If backend doesn't have it, check if it's in sample templates (Phase 1 integration)
  let finalTemplate = backendTemplate;
  if (!backendTemplate && templateId) {
    const sampleTemplate = SAMPLE_TASK_TEMPLATES.find(t => t.templateId === templateId);
    if (sampleTemplate) {
      finalTemplate = sampleTemplate;
    }
  }

  return {
    template: finalTemplate,
    loading: activeQuery.loading,
    error: activeQuery.error || null,
    refetch: activeQuery.refetch
  };
}

export interface UseWorkflowTemplateListOptions {
  tenant?: string;
  domain?: string;
  templateType?: string;
  limit?: number;
  skip?: boolean;
}

export interface UseWorkflowTemplateListResult {
  templates: TemplateListItem[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * List workflow templates for tenant or domain
 *
 * Usage:
 * ```typescript
 * // List all templates for tenant
 * const { templates } = useWorkflowTemplateList({ tenant: 'test' });
 *
 * // List healthcare templates
 * const { templates } = useWorkflowTemplateList({ domain: 'healthcare' });
 *
 * // List specific type
 * const { templates } = useWorkflowTemplateList({
 *   domain: 'healthcare',
 *   templateType: 'patient_admission'
 * });
 * ```
 */
export function useWorkflowTemplateList({
  tenant,
  domain,
  templateType,
  limit,
  skip = false
}: UseWorkflowTemplateListOptions): UseWorkflowTemplateListResult {
  // If domain provided, use domain query (most common use case)
  const domainQuery = useQuery<Record<string, any>>(LIST_WORKFLOW_TEMPLATES_BY_DOMAIN, {
    variables: { domain, templateType, limit },
    skip: skip || !domain
  });

  // If tenant provided (and no domain), use tenant query
  const tenantQuery = useQuery<Record<string, any>>(LIST_WORKFLOW_TEMPLATES_BY_TENANT, {
    variables: { tenant, limit },
    skip: skip || !tenant || !!domain
  });

  // Return whichever query is active
  const activeQuery = domain ? domainQuery : tenantQuery;

  // Get backend templates
  const backendTemplates = activeQuery.data?.listTemplatesByDomain || activeQuery.data?.listTemplatesByTenant || [];

  // Merge with sample Task templates (Phase 1 integration)
  const sampleTemplateListItems = SAMPLE_TASK_TEMPLATES.map((template) => ({
    templateId: template.templateId,
    templateVersion: template.templateVersion,
    domain: template.domain,
    templateType: template.templateType,
    name: template.metadata.business.name,
    category: template.metadata.business.category,
    description: template.metadata.business.description,
    icon: template.metadata.business.icon,
    tags: template.metadata.business.tags,
    usageCount: 0,
    createdAt: template.createdAt,
    updatedAt: template.updatedAt,
  }));

  // Merge backend templates with sample templates (backend takes precedence)
  const backendTemplateIds = new Set(backendTemplates.map((t: any) => t.templateId));
  const uniqueSampleTemplates = sampleTemplateListItems.filter(
    (t) => !backendTemplateIds.has(t.templateId)
  );

  const allTemplates = [...backendTemplates, ...uniqueSampleTemplates];

  return {
    templates: allTemplates,
    loading: activeQuery.loading,
    error: activeQuery.error || null,
    refetch: activeQuery.refetch
  };
}
